/* AVXT Clean, theme v3 (redesign 2026-09-25). One script, loaded with defer.
   1. Dialog helpers (menu, cart drawer, shipping notice, signup popup): native <dialog>
      with showModal(), so focus trapping, Escape and the inert page come from the browser.
   2. Cart drawer: every add-to-cart form posts to /cart/add.js and the drawer re-renders
      through the Section Rendering API. Any failure falls back to a normal form post.
   3. Product page: option pickers (out-of-stock pills struck through), the photo gallery
      (colour products show only the chosen colour's photos), sticky Add to cart on phones
      (names the chosen option).
   4. Shipping notice: opens once per browser on product and collection pages.
   5. Signup popup: a bottom sheet, once per visitor, never on top of another dialog.
      Closed without a signup, it leaves the glowing 10% off tab that reopens it.
   6. Cart page quantities, country picker.
   (The floating cart button needs no script: it is a [data-cart-open] link, see section 2.)
   Storage is always wrapped in try/catch; nothing here is needed to buy. */
/* Mobile speed (2026-10-23, 01-AVXT/Store/2026-10-23-mobile-speed.md). Two things wait until the
   first screen is up, so a phone spends its first second on the first photo:
   a. Photos off the first screen (gallery slides after the first, the video poster, the "More
      from AVXT" cards) carry data-src / data-srcset. They load on the first touch, scroll or key,
      or 2.5 s after the page's load event, whichever comes first; gallery slides then load at
      once (eager) so every photo is there before the swipe.
   b. Judge.me's loader is held (this file runs first, both are defer; its own _loaderExecuted
      flag makes it stop) and its review widget lifted out. Both come back when the widget's
      place comes within 800 px of the screen or its #judgeme_product_reviews link is used.
   Nothing changes in the theme editor. */
(() => {
  'use strict';
  let woke = false;
  const wake = () => {
    if (woke) return;
    woke = true;
    document.querySelectorAll('img[data-src]').forEach((img) => {
      if (img.closest('.gallery__slide')) img.loading = 'eager'; /* ready before the swipe, as before */
      if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  };
  if (document.querySelector('img[data-src]')) {
    ['pointerdown', 'touchstart', 'keydown', 'scroll', 'wheel'].forEach((ev) => window.addEventListener(ev, wake, { once: true, passive: true, capture: true }));
    const later = () => setTimeout(wake, 2500);
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
  }

  if (window.Shopify && window.Shopify.designMode) return;
  const widget = document.querySelector('.jdgm-review-widget[data-entry-point]');
  const loader = Array.from(document.scripts).find((sc) => /judgeme/.test(sc.src) && /loader\.js/.test(sc.src));
  if (!widget || !loader || (window.jdgm && window.jdgm._loaderExecuted)) return;
  window.jdgm = window.jdgm || {};
  window.jdgm._loaderExecuted = true; /* Judge.me's loader checks this flag and stops, so none of its files load yet */
  const block = widget.closest('.shopify-app-block') || widget;
  const slot = document.createElement('div');
  slot.id = widget.id;
  slot.setAttribute('data-jdgm-slot', '');
  widget.removeAttribute('id');
  block.replaceWith(slot);
  let back = false;
  const putBack = () => {
    if (back) return;
    back = true;
    slot.removeAttribute('id');
    widget.id = 'judgeme_product_reviews';
    slot.replaceWith(block);
    window.jdgm._loaderExecuted = false;
    const sc = document.createElement('script');
    sc.src = loader.src;
    document.body.appendChild(sc);
  };
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); putBack(); }
    }, { rootMargin: '800px 0px' });
    io.observe(slot);
  } else {
    window.addEventListener('scroll', putBack, { once: true, passive: true });
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest && e.target.closest('a[href$="#judgeme_product_reviews"]')) putBack();
  }, true);
  if (location.hash === '#judgeme_product_reviews') putBack();
})();
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const canModal = typeof HTMLDialogElement === 'function' && 'showModal' in HTMLDialogElement.prototype;
  let modalShownThisView = false;

  /* ---------- 1. Dialogs ---------- */
  const openDialog = (dlg, opener) => {
    if (!dlg || !canModal) return false;
    if (dlg.open) return true;
    dlg._opener = opener || document.activeElement;
    dlg.showModal();
    modalShownThisView = true;
    return true;
  };
  const closeDialog = (dlg) => {
    if (!dlg || !dlg.open) return;
    dlg.close();
  };
  const bindDialog = (dlg) => {
    if (!dlg || dlg.dataset.bound) return;
    dlg.dataset.bound = '1';
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-dialog-close]')) { closeDialog(dlg); return; }
      if (e.target === dlg) {
        // A click on the backdrop lands on the dialog element itself, outside its box.
        const r = dlg.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) closeDialog(dlg);
      }
    });
    dlg.addEventListener('close', () => {
      const o = dlg._opener;
      if (o && typeof o.focus === 'function' && document.contains(o)) {
        try { o.focus({ preventScroll: true }); } catch (err) { /* nothing to restore */ }
      }
    });
  };

  if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
    $$('.footer__col[open]').forEach((d) => d.removeAttribute('open'));
  }

  const menu = $('[data-menu]');
  bindDialog(menu);
  $$('[data-menu-open]').forEach((b) => b.addEventListener('click', () => {
    if (!openDialog(menu, b)) window.location.href = '/collections/all';
  }));

  /* ---------- 2. Cart drawer ---------- */
  const SECTION = 'avxt-cart-drawer';
  const drawer = () => $('[data-cart-drawer]');
  bindDialog(drawer());

  const setCount = (n) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = n; el.hidden = !n; });
    $$('[data-cart-open]').forEach((el) => el.setAttribute('aria-label', 'Cart, ' + n + ' items'));
  };

  /* Estimated arrival dates (avxt-eta.liquid, wave 2.3 2026-10-22): today's date in Toronto, then
     data-ship business days to ship (Saturday and Sunday skipped), then data-min to data-max
     days in transit (Canada 4 to 7, the US 4 to 9). An order on Thu Oct 22 ships by Tue Oct 27
     and arrives in Canada Oct 31 to Nov 3. Rewrites the sentence only when all three numbers are present. */
  const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
  const addBizDays = (date, n) => {
    const d = new Date(date);
    while (n > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) n -= 1; }
    return d;
  };
  const torontoToday = () => {
    try {
      const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
      const g = (t) => parseInt(p.find((x) => x.type === t).value, 10);
      return new Date(g('year'), g('month') - 1, g('day'), 12);
    } catch (err) { return new Date(); }
  };
  const fmtDate = (d) => d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }).replace('.', '');
  const applyEta = (root) => {
    $$('[data-eta]', root || document).forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      const ship = parseInt(el.dataset.ship, 10);
      if (!(min > 0 && max >= min && ship >= 0)) return;
      const shipped = addBizDays(torontoToday(), ship);
      el.textContent = 'Free shipping to ' + el.dataset.place + '. Arrives ' + fmtDate(addDays(shipped, min)) + ' to ' + fmtDate(addDays(shipped, max)) + '.';
    });
  };
  applyEta();

  const swapDrawer = (html) => {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-drawer-inner]');
    const live = $('[data-drawer-inner]');
    if (!fresh || !live) return false;
    live.replaceWith(fresh);
    applyEta(fresh);
    $$('form[action*="/cart/add"]', fresh).forEach(bindAddForm);
    const n = doc.querySelector('[data-cart-count-src]');
    if (n) setCount(parseInt(n.textContent, 10) || 0);
    return true;
  };

  const renderDrawer = () => fetch(window.location.pathname + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : Promise.reject(r)))
    .then((j) => swapDrawer(j[SECTION]));

  const openDrawer = (opener) => {
    const d = drawer();
    if (!d || !canModal) { window.location.href = '/cart'; return; }
    openDialog(d, opener);
  };

  $$('[data-cart-open]').forEach((a) => a.addEventListener('click', (e) => {
    if (!canModal || !drawer()) return;
    e.preventDefault();
    renderDrawer().catch(() => {}).finally(() => openDrawer(a));
  }));

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-drawer-change]');
    if (!b) return;
    e.preventDefault();
    b.disabled = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(b.dataset.drawerChange), quantity: Number(b.dataset.qty), sections: SECTION })
    }).then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((j) => { if (!swapDrawer(j.sections && j.sections[SECTION])) return renderDrawer(); })
      .catch(() => { window.location.href = '/cart'; });
  });

  const bindAddForm = (form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = '1';
    form.addEventListener('submit', (e) => {
      const by = e.submitter;
      if (by && by.name && by.name !== 'add') return;
      if (!canModal || !drawer() || !window.fetch) return;
      e.preventDefault();
      const buttons = $$('[data-add], [data-sticky-add]').concat(by ? [by] : []);
      const labels = buttons.map((b) => b.textContent);
      buttons.forEach((b) => { b.disabled = true; b.textContent = 'Adding...'; });
      const fd = new FormData(form);
      fd.append('sections', SECTION);
      fetch('/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((j) => {
          if (!swapDrawer(j.sections && j.sections[SECTION])) return renderDrawer();
        })
        .then(() => { if ($('[data-cart-form]')) { window.location.reload(); } else { openDrawer(by || form.querySelector('[name="add"]')); } })
        .catch(() => { form.submit(); })
        .finally(() => buttons.forEach((b, i) => { b.disabled = false; b.textContent = labels[i]; }));
    });
  };
  $$('form[action*="/cart/add"]').forEach(bindAddForm);

  /* ---------- 3. Product page ---------- */
  const pdp = $('[data-pdp]');
  if (pdp) {
    const form = $('[data-product-form]', pdp);
    const idInput = form && $('[data-variant-id]', form);
    const dataEl = $('[data-variants]', pdp);
    const addBtn = $('[data-add]', pdp);
    const stickyAdd = $('[data-sticky-add]', pdp);
    const priceEl = $('[data-price]', pdp);
    const stickyPrice = $('[data-sticky-price]', pdp);
    const stickyVariant = $('[data-sticky-variant]', pdp);
    const track = $('[data-gallery-track]', pdp);
    const gallery = $('[data-gallery]', pdp);

    // Gallery: swipe on phones; arrows, thumbnails and arrow keys everywhere. Dots and the
    // active thumbnail follow a swipe. Past either end it jumps straight to the other end.
    // Colour products show only the chosen colour's photos (owner 2026-09-26): each slide
    // carries the colours its alt text names in data-colors ("|pink|"), and a slide with none
    // shows for every colour. Hidden slides, their thumbnails and dots drop out of the count.
    const allSlides = track ? $$('.gallery__slide', track) : [];
    const allThumbs = $$('[data-thumb]', pdp);
    const allDots = $$('[data-dot]', pdp);
    let gslides = [];
    let thumbs = [];
    let dots = [];
    let gcur = 0;
    const collect = () => {
      gslides = allSlides.filter((s) => !s.hidden);
      thumbs = allThumbs.filter((b) => !b.parentNode.hidden);
      dots = allDots.filter((d) => !d.hidden);
      thumbs.forEach((b, n) => b.setAttribute('aria-label', 'Photo ' + (n + 1) + ' of ' + thumbs.length));
      if (gallery) gallery.classList.toggle('gallery--single', gslides.length < 2);
    };
    const gindex = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    const gpaint = (i) => {
      gcur = i;
      dots.forEach((d, n) => d.classList.toggle('is-on', n === i));
      thumbs.forEach((b, n) => {
        b.classList.toggle('is-on', n === i);
        if (n === i) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
      const on = thumbs[i];
      const strip = on && on.closest('.gallery__thumbs');
      if (on && strip) strip.scrollTo({ left: on.offsetLeft - (strip.clientWidth - on.offsetWidth) / 2, behavior: 'smooth' });
    };
    const greduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ggo = (i) => {
      if (gslides.length < 2) return;
      const wrap = i < 0 || i >= gslides.length;
      const n = (i + gslides.length) % gslides.length;
      gpaint(n);
      track.scrollTo({ left: n * track.clientWidth, behavior: (greduced || wrap) ? 'auto' : 'smooth' });
    };
    const filterColour = (value) => {
      const key = '|' + String(value || '').toLowerCase() + '|';
      allSlides.forEach((s, n) => {
        const tags = s.dataset.colors || '';
        const show = !tags || tags.indexOf(key) !== -1;
        s.hidden = !show;
        // A photo revealed by a colour change loads at once instead of waiting on lazy-load.
        if (show) { const im = $('img', s); if (im && im.loading === 'lazy') im.loading = 'eager'; }
        if (allThumbs[n]) allThumbs[n].parentNode.hidden = !show;
        if (allDots[n]) allDots[n].hidden = !show;
      });
      collect();
    };
    const showSlide = (slide) => {
      const n = Math.max(0, gslides.indexOf(slide));
      gpaint(n);
      track.scrollTo({ left: n * track.clientWidth, behavior: 'auto' });
    };
    if (track && allSlides.length) {
      collect();
      let gt = null;
      track.addEventListener('scroll', () => { clearTimeout(gt); gt = setTimeout(() => gpaint(gindex()), 60); }, { passive: true });
      $$('[data-gallery-prev]', pdp).forEach((b) => b.addEventListener('click', () => ggo(gcur - 1)));
      $$('[data-gallery-next]', pdp).forEach((b) => b.addEventListener('click', () => ggo(gcur + 1)));
      allThumbs.forEach((b) => b.addEventListener('click', () => { const n = thumbs.indexOf(b); if (n !== -1) ggo(n); }));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); ggo(gcur + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); ggo(gcur - 1); }
      });
      window.addEventListener('resize', () => { track.scrollTo({ left: gcur * track.clientWidth }); });
      gpaint(gindex());
    }

    // Character video in gallery slot 2 (wave 2.1, 2026-10-22): plays only on a tap, starts muted,
    // the Sound button turns the sound on. It pauses when the gallery moves to another slide.
    $$('[data-pv]', pdp).forEach((box) => {
      const v = $('video', box);
      const snd = $('[data-pv-sound]', box);
      if (!v) return;
      const sync = () => {
        box.classList.toggle('is-playing', !v.paused);
        if (snd) { snd.textContent = v.muted ? 'Sound on' : 'Sound off'; snd.setAttribute('aria-pressed', String(!v.muted)); }
      };
      const play = () => { const r = v.play(); if (r && r.catch) r.catch(() => {}); };
      $('[data-pv-play]', box).addEventListener('click', play);
      v.addEventListener('click', () => { if (v.paused) play(); else v.pause(); });
      if (snd) snd.addEventListener('click', () => { v.muted = !v.muted; if (v.paused) play(); sync(); });
      ['play', 'pause', 'volumechange'].forEach((ev) => v.addEventListener(ev, sync));
      if (track) track.addEventListener('scroll', () => {
        const r = box.getBoundingClientRect(); const t = track.getBoundingClientRect();
        if (!v.paused && (r.right <= t.left + 1 || r.left >= t.right - 1)) v.pause();
      }, { passive: true });
    });

    if (dataEl && form) {
      let variants = [];
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { variants = []; }
      const colourOpt = gallery && gallery.dataset.colorOpt != null ? Number(gallery.dataset.colorOpt) : -1;
      const current = () => $$('[data-opt]', pdp).map((fs) => {
        const c = $('input:checked', fs);
        return c ? c.value : null;
      });
      // A pill is struck through when no in-stock variant has that value together with the
      // values chosen in the other rows (the same rule Liquid prints on load).
      const markStock = (sel) => {
        $$('[data-opt]', pdp).forEach((fs) => {
          const i = Number(fs.dataset.opt);
          $$('input', fs).forEach((input) => {
            const ok = variants.some((x) => x.available && x.options[i] === input.value && x.options.every((o, k) => k === i || sel[k] == null || o === sel[k]));
            const pill = input.closest('.opt__pill');
            if (pill) pill.classList.toggle('is-out', !ok);
          });
        });
      };
      const apply = () => {
        const sel = current();
        const v = variants.find((x) => x.options.every((o, i) => o === sel[i]));
        sel.forEach((val, i) => { const l = $('[data-opt-label="' + i + '"]', pdp); if (l && val) l.textContent = val; });
        markStock(sel);
        [addBtn, stickyAdd].forEach((b) => {
          if (!b) return;
          b.disabled = !v || !v.available;
          b.textContent = !v ? 'Unavailable' : (v.available ? 'Add to cart' : 'Sold out');
        });
        if (track && colourOpt >= 0 && sel[colourOpt] != null) {
          filterColour(sel[colourOpt]);
          const own = v && v.media ? $('[data-media-id="' + v.media + '"]', track) : null;
          showSlide(own && !own.hidden ? own : gslides[0]);
        }
        if (!v) return;
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = v.price;
        const wasEl = $('[data-was]', pdp);
        if (wasEl) { wasEl.textContent = v.was || ''; wasEl.hidden = !v.was; }
        const saveEl = $('[data-save]', pdp);
        if (saveEl && v.save) saveEl.textContent = v.save;
        const lowEl = $('[data-low]', pdp);
        if (lowEl) {
          lowEl.hidden = false;
          $('[data-low-n]', lowEl).textContent = v.available ? 'In stock' : 'Out of stock';
        }
        if (stickyPrice) stickyPrice.textContent = v.price;
        if (stickyVariant) stickyVariant.textContent = v.title;
        try { window.history.replaceState({}, '', v.url); } catch (err) { /* URL stays as it was */ }
        if (colourOpt < 0 && v.media && track) {
          const slide = $('[data-media-id="' + v.media + '"]', track);
          if (slide && !slide.hidden) {
            const n = gslides.indexOf(slide);
            if (n !== -1) { gpaint(n); track.scrollTo({ left: n * track.clientWidth, behavior: greduced ? 'auto' : 'smooth' }); }
          }
        }
      };
      $$('[data-opt] input', pdp).forEach((r) => r.addEventListener('change', apply));
    }

    // Sticky Add to cart: shows once the main button has scrolled up out of view.
    const bar = $('[data-stickybuy]', pdp);
    if (bar && addBtn && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => {
        const on = !en.isIntersecting || en.intersectionRatio < 0.6;
        bar.classList.toggle('is-on', on);
        document.documentElement.classList.toggle('bar-on', on); // lifts the floating buttons
        bar.setAttribute('aria-hidden', String(!on));
        if (stickyAdd) stickyAdd.tabIndex = on ? 0 : -1;
      }, { threshold: [0, 0.6, 1] }).observe(addBtn);
    }
  }

  // Home: "Shop" bar on phones once the hero button has scrolled away.
  const homeBar = $('[data-home-sticky]');
  const heroCta = $('[data-hero-cta]');
  if (homeBar && heroCta && 'IntersectionObserver' in window) {
    const link = $('a', homeBar);
    new IntersectionObserver(([en]) => {
      const on = !en.isIntersecting && en.boundingClientRect.top < 0;
      homeBar.classList.toggle('is-on', on);
      document.documentElement.classList.toggle('bar-on', on);
      homeBar.setAttribute('aria-hidden', String(!on));
      if (link) link.tabIndex = on ? 0 : -1;
    }).observe(heroCta);
  }

  // Home: product story carousel. Buttons (the row under the slide on phones, the edge
  // arrows on desktop) and arrow keys move one slide; dots and the counter follow a swipe.
  // It loops: a copy of the last slide sits before the first and a copy of the first after
  // the last, so Next on the last slide glides forward into slide one and Prev on the first
  // glides back into the last (owner 2026-09-25: "it should loop smoothly"). Once that glide
  // settles, the track re-parks on the real slide, which is pixel-identical, so nothing
  // visible happens. Slots: 0 = copy of last, 1..n = the real slides, n + 1 = copy of first.
  $$('[data-carousel]').forEach((c) => {
    const track = $('[data-carousel-track]', c);
    const slides = $$('[data-carousel-slide]', c);
    const dots = $$('[data-carousel-dot]', c);
    const count = $('[data-carousel-count]', c);
    if (!track || slides.length < 2) return;
    const n = slides.length;
    const copy = (s) => {
      const k = s.cloneNode(true);
      k.removeAttribute('data-carousel-slide');
      k.removeAttribute('aria-label');
      k.setAttribute('aria-hidden', 'true');
      k.classList.add('carousel__slide--copy');
      $$('a, button, [tabindex]', k).forEach((el) => { el.tabIndex = -1; });
      $$('img', k).forEach((im) => { im.loading = 'eager'; });
      return k;
    };
    track.insertBefore(copy(slides[n - 1]), slides[0]);
    track.appendChild(copy(slides[0]));
    const w = () => Math.max(1, track.clientWidth);
    const slot = () => Math.round(track.scrollLeft / w());
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let cur = 0;
    const paint = (i) => {
      cur = i;
      dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
      if (count) count.textContent = (i + 1) + ' / ' + n;
    };
    const park = () => {
      // After a glide or a swipe lands on a copy, step to the real slide without animation.
      const s = slot();
      if (s === 0) track.scrollTo({ left: n * w(), behavior: 'auto' });
      else if (s === n + 1) track.scrollTo({ left: w(), behavior: 'auto' });
      paint(((slot() - 1) % n + n) % n);
    };
    const go = (i) => {
      // A click mid-glide first settles on the current slide, so two quick presses never
      // run backwards across the track.
      track.scrollTo({ left: (cur + 1) * w(), behavior: 'auto' });
      paint(((i % n) + n) % n);
      const target = reduced ? cur + 1 : i + 1;
      track.scrollTo({ left: target * w(), behavior: reduced ? 'auto' : 'smooth' });
    };
    let t = null;
    track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(park, 120); }, { passive: true });
    $$('[data-carousel-prev]', c).forEach((b) => b.addEventListener('click', () => go(cur - 1)));
    $$('[data-carousel-next]', c).forEach((b) => b.addEventListener('click', () => go(cur + 1)));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(cur + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(cur - 1); }
    });
    window.addEventListener('resize', () => { track.scrollTo({ left: (cur + 1) * w(), behavior: 'auto' }); });
    track.scrollLeft = w();
    paint(0);
  });

  /* ---------- 4. Shipping notice ---------- */
  const SHIP_KEY = 'avxt-ship-notice-v1';
  const seen = () => {
    try { if (window.localStorage.getItem(SHIP_KEY)) return true; } catch (err) { /* storage blocked */ }
    return document.cookie.split('; ').some((c) => c.indexOf(SHIP_KEY + '=') === 0);
  };
  const markSeen = () => {
    try { window.localStorage.setItem(SHIP_KEY, String(Date.now())); } catch (err) { /* storage blocked */ }
    try { document.cookie = SHIP_KEY + '=1; max-age=31536000; path=/; SameSite=Lax'; } catch (err) { /* cookies blocked */ }
  };
  const notice = $('[data-ship-notice]');
  if (notice) {
    bindDialog(notice);
    notice.addEventListener('close', markSeen);
    $$('[data-ship-notice-open]').forEach((b) => b.addEventListener('click', () => openDialog(notice, b)));
    if (notice.dataset.auto === 'true' && !seen() && canModal) {
      // Let the page paint first so the product shows behind the sheet.
      window.setTimeout(() => {
        if (!document.querySelector('dialog[open]')) openDialog(notice);
      }, 700);
    }
  } else {
    $$('[data-ship-notice-open]').forEach((b) => { b.hidden = true; });
  }

  /* ---------- 5. Signup popup ---------- */
  const pop = $('[data-signup]');
  if (pop) {
    bindDialog(pop);
    // Wave 2.5 (2026-10-22): a small bottom sheet opened with show(), not showModal(), so the
    // page stays usable behind it; Escape still closes it.
    const showPop = (opener) => {
      if (!canModal || pop.open) return;
      pop._opener = opener || document.activeElement;
      pop.show();
    };
    pop.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDialog(pop); });
    const KEY = 'avxt-signup-2026-09';
    const DAY = 86400000;
    const until = () => {
      try { const n = parseInt(window.localStorage.getItem(KEY), 10); return Number.isFinite(n) ? n : 0; } catch (err) { return 0; }
    };
    const rest = (days) => { try { window.localStorage.setItem(KEY, String(Date.now() + days * DAY)); } catch (err) { /* storage blocked */ } };
    const form = $('form', pop);
    if (form) form.addEventListener('submit', () => rest(3650));
    pop.addEventListener('close', () => { if (until() < Date.now() + 365 * DAY) rest(30); });

    // 10% off tab (avxt-float-actions.liquid, owner 2026-09-28): after the popup is closed without a
    // signup ("No thanks", x, Escape, backdrop) it glows and bounces on every page until the rest runs
    // out, and reopens the popup. A signup rests the popup ten years, so the tab never shows again.
    // Its x hides it for the rest of the visit.
    const tab = $('[data-offer-tab]');
    if (tab) {
      const TAB_KEY = 'avxt-offer-tab-hidden';
      const posted = !!$('[data-signup-posted]', pop);
      let closedHere = false;
      const hiddenThisVisit = () => { try { return window.sessionStorage.getItem(TAB_KEY) === '1'; } catch (err) { return false; } };
      const syncTab = () => {
        const u = until();
        const now = Date.now();
        const notSignedUp = !posted && u < now + 365 * DAY;
        tab.hidden = !(notSignedUp && (u > now || closedHere)) || hiddenThisVisit();
      };
      // Watch the open attribute rather than the close event: it changes the moment the popup
      // closes by any route, and the close event can lag (it never fired in a background tab).
      new MutationObserver(() => {
        if (pop.open) return;
        if (until() < Date.now() + 365 * DAY) rest(30);
        closedHere = !posted;
        syncTab();
      }).observe(pop, { attributes: true, attributeFilter: ['open'] });
      $('[data-offer-reopen]', tab).addEventListener('click', (e) => { showPop(e.currentTarget); });
      $('[data-offer-hide]', tab).addEventListener('click', () => {
        try { window.sessionStorage.setItem(TAB_KEY, '1'); } catch (err) { /* storage blocked */ }
        tab.hidden = true;
      });
      syncTab();
    }

    // It opens on its own once per visitor (SHOWN_KEY), after 20 seconds or half the page
    // scrolled, and waits while another dialog (menu, cart drawer, shipping notice) is open.
    const SHOWN_KEY = 'avxt-signup-shown-2026-10';
    const shownBefore = () => { try { return window.localStorage.getItem(SHOWN_KEY) === '1'; } catch (err) { return false; } };
    if ($('[data-signup-posted]', pop)) {
      rest(3650);
      showPop();
    } else if (pop.dataset.auto === 'true' && Date.now() >= until() && !shownBefore() && canModal) {
      let fired = false;
      let waiting = false;
      const fire = () => {
        if (fired || waiting) return;
        if (document.querySelector('dialog[open]')) { waiting = true; window.setTimeout(() => { waiting = false; fire(); }, 5000); return; }
        fired = true;
        window.removeEventListener('scroll', onScroll);
        try { window.localStorage.setItem(SHOWN_KEY, '1'); } catch (err) { /* storage blocked */ }
        showPop();
      };
      const onScroll = () => {
        const room = document.documentElement.scrollHeight - window.innerHeight;
        if (room > 0 && window.scrollY / room >= 0.5) fire();
      };
      window.setTimeout(fire, 20000);
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* ---------- 6. Cart page and country picker ---------- */
  const cartForm = $('[data-cart-form]');
  if (cartForm) {
    let timer = null;
    const post = (input) => {
      const line = parseInt(input.dataset.line, 10);
      const qty = parseInt(input.value, 10);
      if (Number.isNaN(line) || Number.isNaN(qty) || qty < 0) return;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: qty })
      }).then((r) => (r.ok ? window.location.reload() : Promise.reject(r)))
        .catch(() => cartForm.submit());
    };
    $$('input[name="updates[]"]', cartForm).forEach((input) => input.addEventListener('change', () => {
      clearTimeout(timer);
      timer = setTimeout(() => post(input), 350);
    }));
    $$('[data-qty-step]', cartForm).forEach((b) => b.addEventListener('click', () => {
      const input = $('input[name="updates[]"]', b.parentNode);
      if (!input) return;
      input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(b.dataset.qtyStep));
      input.dispatchEvent(new Event('change'));
    }));
  }

  $$('[data-loc]').forEach((sel) => sel.addEventListener('change', () => { if (sel.form) sel.form.submit(); }));
})();
